<template>
  <div class="col-12 ">
    <GroupStudentFilter
      :filters="filters"
      @update:filters="updateFilters"
      @apply-filters="applyFilters"
      @reset-filters="resetFilters"
      :wideLayout="true"
    />
    
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ $t('groupStudents.title', { group: group?.name || $t('groupStudents.group') }) }}
            </h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2 pe-3">
        <div class="table-responsive p-4">
          <LoadingSpinner 
            v-if="isLoading" 
            :message="$t('groupStudents.loading')"
          />
          
          <!-- Mobile View -->
          <div class="d-block d-md-none">
            <div v-if="!isLoading">
              <div v-for="student in students" :key="'mobile-'+student.id" class="card mb-3">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="mb-0 fw-medium">
                      {{ student.first_name }} {{ student.father_name }} {{ student.last_name }}
                    </h6>
                    <span class="badge" :class="student.gender === 'M' ? 'bg-info' : 'bg-pink'">
                      {{ student.gender === 'M' ? $t('groupStudents.male') : $t('groupStudents.female') }}
                    </span>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <small class="text-muted">{{ $t('groupStudents.nationalNum') }}:</small>
                      <p>{{ student.national_num || $t('groupStudents.undefined') }}</p>
                    </div>
                    <div class="col-6">
                      <small class="text-muted">{{ $t('groupStudents.registerDate') }}:</small>
                      <p>{{ formatDate(student.register_date) }}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div v-if="students.length === 0" class="text-center py-4 text-muted">
                <i class="fas fa-users-slash fa-2x mb-2"></i>
                <p>{{ $t('groupStudents.noStudents') }}</p>
              </div>
            </div>
          </div>
          
          <!-- Desktop View -->
          <table v-if="!isLoading" class="table align-items-center mb-0 d-none d-md-table">
            <thead>
              <tr>
                <th class="text-uppercase text-xxs opacity-7">{{ $t('groupStudents.fullName') }}</th>
                <th class="text-uppercase text-xxs opacity-7">{{ $t('groupStudents.gender') }}</th>
                <th class="text-uppercase text-xxs opacity-7">{{ $t('groupStudents.nationalNum') }}</th>
                <th class="text-uppercase text-xxs opacity-7">{{ $t('groupStudents.registerDate') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="student in students" :key="student.id">
                <td>
                  <div class="d-flex px-3 py-1">
                    <div class="d-flex flex-column justify-content-center">
                      <h6 class="mb-0 text-md fw-medium">
                        {{ student.first_name }} {{ student.father_name }} {{ student.last_name }}
                      </h6>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="badge" :class="student.gender === 'M' ? 'bg-info' : 'bg-pink'">
                    {{ student.gender === 'M' ? $t('groupStudents.male') : $t('groupStudents.female') }}
                  </span>
                </td>
                <td>
                  {{ student.national_num || $t('groupStudents.undefined') }}
                </td>
                <td>
                  {{ formatDate(student.register_date) }}
                </td>
              </tr>
              <tr v-if="students.length === 0">
                <td colspan="4" class="text-center py-4 text-muted">
                  <i class="fas fa-users-slash fa-2x mb-2"></i>
                  <p>{{ $t('groupStudents.noStudents') }}</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    
    <div class="pagination-container" v-if="pagination.totalItems > 10">
      <div class="pagination-wrapper">
        <button
          @click="goToPage(1)"
          :disabled="currentPage === 1"
          class="page-button first-last"
          :aria-label="$t('groupStudents.firstPage')"
        >
          <i class="fas fa-angle-double-right"></i>
        </button>

        <!-- Previous Button -->
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="page-button prev-next"
          :aria-label="$t('groupStudents.prev')"
        >
          <i class="fas fa-angle-right"></i>
        </button>

        <div class="page-numbers">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="{ active: page === currentPage }"
            class="page-button number"
          >
            {{ page }}
          </button>
        </div>

        <!-- Next Button -->
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === lastPage"
          class="page-button prev-next"
          :aria-label="$t('groupStudents.next')"
        >
          <i class="fas fa-angle-left"></i>
        </button>

        <!-- Last Page Button -->
        <button
          @click="goToPage(lastPage)"
          :disabled="currentPage === lastPage"
          class="page-button first-last"
          :aria-label="$t('groupStudents.lastPage')"
        >
          <i class="fas fa-angle-double-left"></i>
        </button>
      </div>

      <div class="items-count">
        {{ $t('groupStudents.showing') }} {{ from }} - {{ to }} {{ $t('groupStudents.of') }} {{ total }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import GroupStudentFilter from "@/components/features/students/filters/GroupStudentFilter.vue";

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();

const isLoading = computed(() => store.state.groupstudent.isLoading);
const students = computed(() => store.state.groupstudent.students);
const group = computed(() => store.state.groupstudent.currentGroup);
const pagination = computed(() => store.state.groupstudent.pagination);
const filters = computed(() => store.state.groupstudent.filters);
const currentPage = computed(() => {
  const page = pagination.value.current_page;
  return Array.isArray(page) ? page[0] : page;
});

const lastPage = computed(() => {
  const page = pagination.value.last_page;
  return Array.isArray(page) ? page[0] : page;
});

const from = computed(() => {
  const from = pagination.value.from;
  return Array.isArray(from) ? from[0] : from;
});

const to = computed(() => {
  const to = pagination.value.to;
  return Array.isArray(to) ? to[0] : to;
});

const total = computed(() => {
  const total = pagination.value.total;
  return Array.isArray(total) ? total[0] : total;
});
const visiblePages = computed(() => {
  const pages = [];
  const range = 2;

  for (
    let i = Math.max(1, currentPage.value - range);
    i <= Math.min(lastPage.value, currentPage.value + range);
    i++
  ) {
    pages.push(i);
  }
  return pages;
});
const fetchGroupStudents = async (page = null, perPage = null) => {
  try {
    if (page !== null) {
      store.commit('groupstudent/SET_PAGINATION', {
        ...pagination.value,
        current_page: page,
      });
    }

    if (perPage !== null) {
      store.commit('groupstudent/SET_PAGINATION', {
        ...pagination.value,
        per_page: perPage,
        current_page: 1,
      });
    }

    const response = await store.dispatch('groupstudent/fetchGroupStudents', {
      classId: route.params.classId,
      groupId: route.params.groupId,
    });
    
    if (response?.meta) {
      store.commit('groupstudent/SET_PAGINATION', {
        current_page: response.meta.current_page,
        per_page: response.meta.per_page,
        total: response.meta.total,
        last_page: response.meta.last_page,
        from: response.meta.from,
        to: response.meta.to,
      });
    }
  } catch (err) {
    console.error('Error:', err);
    if (err.response?.status === 404) {
      toast.error(t('groupStudents.groupNotFound'));
    }
  }
};

const goToPage = (page) => {
  fetchGroupStudents(page);
};

const updateFilters = (newFilters) => {
  store.commit('groupstudent/SET_FILTERS', newFilters);
};

const applyFilters = async () => {
  try {
    store.commit('groupstudent/SET_PAGINATION', {
      ...pagination.value,
      current_page: 1
    });
    
    await fetchGroupStudents();
  } catch (error) {
    toast.error(t('groupStudents.filterError'));
    console.error('Apply filters error:', error);
  }
};
const resetFilters = () => {
  try {
    store.commit('groupstudent/RESET_FILTERS');
    store.commit('groupstudent/SET_FILTERS', {
      class_id: route.params.classId,
      group_id: route.params.groupId,
    });
    fetchGroupStudents(1, pagination.value.per_page);
  } catch (error) {
    toast.error(t('groupStudents.resetError'));
    console.error('Reset filters error:', error);
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(locale.value || 'ar', options);
  } catch (error) {
    console.error("Date formatting error:", error);
    return dateString; 
  }
}

watch(() => route.params, (newParams) => {
  if (newParams.classId && newParams.groupId) {
    fetchGroupStudents();
  }
});

onMounted(() => {
  if (!route.params.classId || !route.params.groupId) {
    toast.warning(t('groupStudents.noGroupOrClass'));
    router.push({ name: 'ClassGroups' });
    return;
  }
  store.commit('groupstudent/SET_FILTERS', {
    class_id: route.params.classId,
    group_id: route.params.groupId
  });

  fetchGroupStudents();
  try {
    fetchGroupStudents();
    store.dispatch("classes/fetchClasses");
  } catch (error) {
    toast.error(t('groupStudents.initialLoadError'));
    console.error("Initial load error:", error);
  }
});
</script>